// podtemat2_maturalne_zamkniete_types/MaturaPowerDivisionClosedTask.js
// Zadanie maturalne zamknięte: dzielenie potęg typu 25^{-2} / 125^{-4},
// uogólnione na rodziny dla baz 2, 3 i 5 (2,4,8; 3,9,27; 5,25,125).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { buildDistractorExponents } from "./MaturaPowerDivisionClosedTask.logic";

// pomocnicze
const LETTERS = ["A", "B", "C", "D"];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const expToLatex = (e) => (e < 0 ? `-${Math.abs(e)}` : `${e}`);

// rodziny liczb: num to liczba, powMul – ile razy trzeba pomnożyć wykładnik,
// by przejść na wspólną podstawę (np. 25 = 5^2 → powMul = 2)
const FAMILIES = {
  2: [
    { num: 2, powMul: 1 },
    { num: 4, powMul: 2 },
    { num: 8, powMul: 3 },
  ],
  3: [
    { num: 3, powMul: 1 },
    { num: 9, powMul: 2 },
    { num: 27, powMul: 3 },
  ],
  5: [
    { num: 5, powMul: 1 },
    { num: 25, powMul: 2 },
    { num: 125, powMul: 3 },
  ],
};

// dozwolone wykładniki w liczniku/mianowniku (unikamy 0 i ±1)
const EXP_POOL = [-5, -4, -3, -2, 2, 3, 4, 5];

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 2:
 *   25^{-2} / 125^{-4}  →  D. 5^{8}
 */
function createOriginalTask() {
  const base = 5;
  const top = { num: 25, powMul: 2 };
  const bottom = { num: 125, powMul: 3 };
  const e1 = -2;
  const e2 = -4;
  const K_top = top.powMul * e1; // -4
  const K_bottom = bottom.powMul * e2; // -12
  const K = K_top - K_bottom; // 8
  const leftLatex = `\\dfrac{25^{-2}}{125^{-4}}`;

  const options = [
    { label: "A", latex: "5^{-16}", isCorrect: false },
    { label: "B", latex: "5^{-2}", isCorrect: false },
    { label: "C", latex: "5^{4}", isCorrect: false },
    { label: "D", latex: "5^{8}", isCorrect: true },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaPowerDivisionClosedTask",
    data: {
      base,
      top,
      bottom,
      e1,
      e2,
      K,
      K_top,
      K_bottom,
      leftLatex,
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 2,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let base, top, bottom, e1, e2, K_top, K_bottom, K, leftLatex;

  const bases = Object.keys(FAMILIES).map((b) => Number(b));
  let tries = 0;

  // losujemy tak długo, aż dostaniemy sensowny (nietrywialny) przypadek
  while (tries < 200) {
    tries += 1;

    base = pick(bases); // 2, 3 lub 5
    const family = FAMILIES[base];

    // różne liczby w liczniku i mianowniku (żeby nie było 25^a / 25^b)
    top = pick(family);
    bottom = pick(family);
    if (top.num === bottom.num) continue;

    e1 = pick(EXP_POOL);
    e2 = pick(EXP_POOL);

    K_top = top.powMul * e1;
    K_bottom = bottom.powMul * e2;
    K = K_top - K_bottom;

    // unikamy zbyt prostych wyników: 0, ±1, ±2 oraz zbyt małej trudności
    if (K === 0 || Math.abs(K) <= 2) continue;
    // ograniczamy też wielkość wykładnika, żeby nie wychodziły potęgi typu 5^20
    if (Math.abs(K) > 10) continue;

    leftLatex = `\\dfrac{${top.num}^{${expToLatex(
      e1
    )}}}{${bottom.num}^{${expToLatex(e2)}}}`;

    break;
  }

  // poprawna odpowiedź
  const correctLatex = `${base}^{${expToLatex(K)}}`;

  const distractorExps = buildDistractorExponents({
    K,
    K_top,
    K_bottom,
    e1,
    e2,
  });

  // w razie czego dobieramy losowo
  while (distractorExps.size < 3) {
    const r = pick([-10, -9, -8, -7, -6, -5, -4, -3, 3, 4, 5, 6, 7, 8, 9, 10]);
    if (r !== K) distractorExps.add(r);
  }

  const optionsRaw = [
    { latex: correctLatex, isCorrect: true },
    ...Array.from(distractorExps).map((v) => ({
      latex: `${base}^{${expToLatex(v)}}`,
      isCorrect: false,
    })),
  ];

  // tasowanie odpowiedzi
  const shuffled = optionsRaw
    .map((o) => ({ ...o, sortKey: Math.random() }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((o, idx) => ({
      label: LETTERS[idx],
      latex: o.latex,
      isCorrect: o.isCorrect,
    }));

  const correctIndex = shuffled.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaPowerDivisionClosedTask",
    data: {
      base,
      top,
      bottom,
      e1,
      e2,
      K,
      K_top,
      K_bottom,
      leftLatex,
      options: shuffled,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_2" },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Liczba <InlineMath math={data.leftLatex} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];

  // tylko A./B./C./D. z wynikiem
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}


export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { base, top, bottom, e1, e2, K, K_top, K_bottom, leftLatex } = data;

  const step1Top = `${top.num}= ${base}^{${top.powMul}}`;
  const step1Bottom = `${bottom.num}= ${base}^{${bottom.powMul}}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Rozpoznajemy liczby jako potęgi podstawy{" "}
        <InlineMath math={`${base}`} />:
      </div>
      <BlockMath math={step1Top} />
      <BlockMath math={step1Bottom} />

      <div>
        <strong>Krok 2.</strong> Podstawiamy te równości do naszego wyrażenia:
      </div>
      <BlockMath
        math={`${leftLatex}=\\dfrac{(${base}^{${top.powMul}})^{${expToLatex(
          e1
        )}}}{(${base}^{${bottom.powMul}})^{${expToLatex(e2)}}}`}
      />

      <div>
        <strong>Krok 3.</strong> Używamy wzoru{" "}
        <InlineMath math={'(a^{m})^{n}=a^{m\\cdot n}'} /> i{" "}
        <InlineMath math={'\\dfrac{a^{p}}{a^{q}}=a^{p-q}'} />:
      </div>
      <BlockMath
        math={`=\\dfrac{${base}^{${K_top}}}{${base}^{${K_bottom}}}= ${base}^{${K_top}-(${K_bottom})}`}
      />
      <BlockMath math={`= ${base}^{${expToLatex(K)}}`} />

      <div>
        <strong>Wynik:</strong>{" "}
        <InlineMath math={`${base}^{${expToLatex(K)}}`} />
      </div>
    </div>
  );
}
